/**
 * Form pieces shared by customer prices and campaign items (#3034): the price type with its
 * unit, the value, the validity window and the server's warnings.
 */

import { useTranslation } from 'react-i18next';
import { Alert, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { PricingWarning, RulePriceType } from '@/hooks/usePartPricing';
import { checkWindow, eurosInputToCents4, formatEuros, formatPercent, percentInput, type DecimalFailure, type WindowFailure } from './decimal';
import { CategoryName, CategoryPicker, PartName, PartPicker, type PickedPart } from './pickers';

export interface RuleValueDraft {
  priceType: RulePriceType;
  /** What the user typed: euros for a fixed price, percent for a discount. */
  valueText: string;
}

/** The canonical value text for the API, or the reason the entry cannot be used (blank is a failure, never 0). */
export function parseRuleValue(draft: RuleValueDraft): { ok: true; value: string } | { ok: false; error: DecimalFailure } {
  return draft.priceType === 'fixed_price' ? eurosInputToCents4(draft.valueText) : percentInput(draft.valueText, 'discount');
}

interface RuleValueFieldsProps {
  draft: RuleValueDraft;
  onChange: (draft: RuleValueDraft) => void;
  disabled?: boolean | undefined;
  /** Shown once the user has touched the field or tried to save. */
  showErrors: boolean;
}

export function RuleValueFields({ draft, onChange, disabled, showErrors }: RuleValueFieldsProps) {
  const { t } = useTranslation('partPricing');
  const parsed = parseRuleValue(draft);
  const failure = parsed.ok ? null : parsed.error;
  const fixed = draft.priceType === 'fixed_price';
  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={draft.priceType}
        disabled={disabled}
        aria-label={t('fields.priceType')}
        onChange={(_event, next: RulePriceType | null) => {
          // A value typed for one unit is not carried over to the other: 20 € is not 20 %.
          if (next && next !== draft.priceType) onChange({ priceType: next, valueText: '' });
        }}
      >
        <ToggleButton value="fixed_price">{t('priceType.fixed_price')}</ToggleButton>
        <ToggleButton value="discount_percent">{t('priceType.discount_percent')}</ToggleButton>
      </ToggleButtonGroup>
      <TextField
        size="small"
        label={fixed ? t('fields.fixedPrice') : t('fields.discountPercent')}
        value={draft.valueText}
        disabled={disabled}
        required
        onChange={(event) => onChange({ ...draft, valueText: event.target.value })}
        error={showErrors && failure !== null}
        helperText={showErrors && failure !== null ? t(`validation.${fixed ? 'money' : 'percent'}.${failure}`) : t(fixed ? 'hints.fixedPrice' : 'hints.discountPercent')}
        slotProps={{
          htmlInput: { inputMode: 'decimal' },
          input: { endAdornment: <InputAdornment position="end">{fixed ? t('units.euroNet') : t('units.percent')}</InputAdornment> },
        }}
      />
    </Stack>
  );
}

interface WindowFieldsProps {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
  allowOpen: boolean;
  maxDays?: number;
  fromLabel: string;
  toLabel: string;
  disabled?: boolean | undefined;
  showErrors: boolean;
}

export function windowErrorKey(failure: WindowFailure | 'required' | null): string | null {
  return failure === null ? null : `validation.window.${failure}`;
}

export function WindowFields({ from, to, onChange, allowOpen, maxDays, fromLabel, toLabel, disabled, showErrors }: WindowFieldsProps) {
  const { t } = useTranslation('partPricing');
  const failure = checkWindow(from, to, { allowOpen, ...(maxDays !== undefined ? { maxDays } : {}) });
  const message = showErrors && failure ? t(windowErrorKey(failure) as string, { max: maxDays }) : null;
  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          size="small"
          type="date"
          fullWidth
          label={fromLabel}
          value={from}
          disabled={disabled}
          required={!allowOpen}
          onChange={(event) => onChange({ from: event.target.value, to })}
          error={message !== null}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          fullWidth
          label={toLabel}
          value={to}
          disabled={disabled}
          required={!allowOpen}
          onChange={(event) => onChange({ from, to: event.target.value })}
          error={message !== null}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      {message !== null ? (
        <Typography variant="caption" color="error">
          {message}
        </Typography>
      ) : (
        allowOpen && (
          <Typography variant="caption" color="text.secondary">
            {t('hints.openWindow')}
          </Typography>
        )
      )}
    </Stack>
  );
}

/** Rule value as a user reads it: net euros or percent, with the unit spelled out. */
export function useRuleValueText() {
  const { t, i18n } = useTranslation('partPricing');
  return (priceType: RulePriceType, value: string): string =>
    priceType === 'fixed_price'
      ? t('rule.fixedValue', { amount: formatEuros(value, i18n.language) })
      : t('rule.discountValue', { percent: formatPercent(value, i18n.language) });
}

/** The server's "special price above catalog" warnings, in words (never the server's prose). */
export function PricingWarnings({ warnings }: { warnings: readonly PricingWarning[] }) {
  const { t, i18n } = useTranslation('partPricing');
  if (warnings.length === 0) return null;
  return (
    <Stack spacing={1}>
      {warnings.map((warning, index) => (
        <Alert severity="warning" key={`${warning.code}-${warning.partId ?? warning.categoryId ?? ''}-${index}`}>
          {warning.target === 'part' ? (
            <>
              {t('warnings.aboveCatalogPart', {
                special: formatEuros(warning.specialNetCents, i18n.language),
                catalog: formatEuros(warning.catalogNetCents, i18n.language),
              })}{' '}
              {warning.partId ? <PartName id={warning.partId} /> : null}
            </>
          ) : (
            <>
              {t('warnings.aboveCatalogCategory', {
                special: formatEuros(warning.specialNetCents, i18n.language),
                count: warning.affectedParts ?? 0,
              })}{' '}
              <CategoryName id={warning.categoryId ?? null} />
            </>
          )}
        </Alert>
      ))}
    </Stack>
  );
}

export interface TargetDraft {
  kind: 'part' | 'category';
  part: PickedPart | null;
  categoryId: string | null;
}

export const emptyTarget: TargetDraft = { kind: 'part', part: null, categoryId: null };

/** Exactly one of `partId` / `categoryId` for the request, or null while nothing is chosen. */
export function targetBody(draft: TargetDraft): { partId: string } | { categoryId: string } | null {
  if (draft.kind === 'part') return draft.part ? { partId: draft.part.id } : null;
  return draft.categoryId ? { categoryId: draft.categoryId } : null;
}

interface TargetFieldsProps {
  draft: TargetDraft;
  onChange: (draft: TargetDraft) => void;
  disabled?: boolean | undefined;
  showErrors: boolean;
}

/** One target: a part (found by search) or a category (found by name). Never an id typed by hand. */
export function TargetFields({ draft, onChange, disabled, showErrors }: TargetFieldsProps) {
  const { t } = useTranslation('partPricing');
  const missing = showErrors && targetBody(draft) === null;
  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={draft.kind}
        disabled={disabled}
        aria-label={t('fields.target')}
        onChange={(_event, next: 'part' | 'category' | null) => {
          if (next && next !== draft.kind) onChange({ ...draft, kind: next });
        }}
      >
        <ToggleButton value="part">{t('targetKind.part')}</ToggleButton>
        <ToggleButton value="category">{t('targetKind.category')}</ToggleButton>
      </ToggleButtonGroup>
      {draft.kind === 'part' ? (
        <PartPicker label={t('fields.part')} value={draft.part} disabled={disabled} onChange={(part) => onChange({ ...draft, part })} />
      ) : (
        <CategoryPicker
          label={t('fields.category')}
          value={draft.categoryId}
          disabled={disabled}
          helperText={t('hints.categoryTarget')}
          onChange={(categoryId) => onChange({ ...draft, categoryId })}
        />
      )}
      {missing && (
        <Typography variant="caption" color="error">
          {t(draft.kind === 'part' ? 'validation.target.part' : 'validation.target.category')}
        </Typography>
      )}
    </Stack>
  );
}
